import React, { useState } from 'react';
import { Upload, FileText, Download, AlertCircle, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { apbsNameMatchingApi, NameMatchingSummary } from '../../services/apbs-name-matching.api';

const ApbsNameMatchingUpload: React.FC = () => {
  const [requestFile, setRequestFile] = useState<File | null>(null);
  const [responseFile, setResponseFile] = useState<File | null>(null);
  const [workStage, setWorkStage] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NameMatchingSummary | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleUpload = async () => {
    if (!requestFile || !responseFile) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const summary = await apbsNameMatchingApi.uploadFiles({
        requestFile,
        responseFile,
        uploadedBy: user.email || 'admin',
        workStage: workStage || undefined,
      });
      setResult(summary);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Upload failed';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!result?.batchId) return;
    setDownloading(true);
    try {
      const blob = await apbsNameMatchingApi.downloadReport(result.batchId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NameMatching_${result.batchId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const resetForm = () => {
    setRequestFile(null);
    setResponseFile(null);
    setWorkStage('');
    setError(null);
    setResult(null);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">APBS Name Matching</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload REQUEST and RESPONSE .txt files to perform fuzzy name matching analysis
        </p>
      </div>

      {/* Upload Section */}
      {!result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" />
            Upload Files
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Request File */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                REQUEST File (.txt)
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".txt"
                  onChange={(e) => setRequestFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-indigo-50 file:text-indigo-700
                    hover:file:bg-indigo-100
                    disabled:opacity-50"
                />
                {requestFile && (
                  <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {requestFile.name} ({(requestFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>

            {/* Response File */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                RESPONSE File (.txt)
              </label>
              <div className="relative">
                <input
                  type="file"
                  accept=".txt"
                  onChange={(e) => setResponseFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2.5 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100
                    disabled:opacity-50"
                />
                {responseFile && (
                  <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {responseFile.name} ({(responseFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Work Stage */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Work Stage (optional - auto-detected if blank)
            </label>
            <select
              value={workStage}
              onChange={(e) => setWorkStage(e.target.value)}
              disabled={uploading}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
            >
              <option value="">Auto-detect</option>
              <option value="BL">BL - Basement Level</option>
              <option value="RL">RL - Roof Level</option>
              <option value="RC">RC - Roof Casting</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!requestFile || !responseFile || uploading}
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-blue-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing... (this may take a few minutes)
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload & Analyze
              </>
            )}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Upload Failed</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Success Banner */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-sm font-semibold text-green-800">Analysis Complete</h3>
                <p className="text-xs text-green-700">
                  Batch: {result.batchId} | {result.totals.totalRecords} records processed in {(result.processingTimeMs / 1000).toFixed(1)}s
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownloadReport}
                disabled={downloading}
                className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Download Excel Report
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-semibold rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                New Upload
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Records" value={result.totals.totalRecords.toLocaleString()} color="indigo" />
            <SummaryCard
              label="Transaction Success"
              value={`${result.transactionStatus.successRate}%`}
              sub={`${result.transactionStatus.successCount.toLocaleString()} / ${result.totals.totalRecords.toLocaleString()}`}
              color="green"
            />
            <SummaryCard
              label="Name Match Rate"
              value={`${result.nameMatching.successRate}%`}
              sub={`Exact + High matches`}
              color="blue"
            />
            <SummaryCard
              label="Total Paid"
              value={`Rs ${Number(result.transactionStatus.totalPaidAmountInRupees).toLocaleString()}`}
              color="emerald"
            />
          </div>

          {/* Name Matching Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Name Matching Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MatchCard label="Exact Match" count={result.nameMatching.exactMatchCount} total={result.totals.totalRecords} color="bg-green-500" />
              <MatchCard label="High Match" count={result.nameMatching.highMatchCount} total={result.totals.totalRecords} color="bg-blue-500" />
              <MatchCard label="Partial Match" count={result.nameMatching.partialMatchCount} total={result.totals.totalRecords} color="bg-yellow-500" />
              <MatchCard label="Low Match" count={result.nameMatching.lowMatchCount} total={result.totals.totalRecords} color="bg-orange-500" />
              <MatchCard label="Mismatch" count={result.nameMatching.mismatchCount} total={result.totals.totalRecords} color="bg-red-500" />
            </div>
          </div>

          {/* Transaction Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Transaction Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Transaction Status</h3>
              <div className="space-y-3">
                <StatusRow label="Success" count={result.transactionStatus.successCount} total={result.totals.totalRecords} color="green" />
                <StatusRow label="Failed" count={result.transactionStatus.failedCount} total={result.totals.totalRecords} color="red" />
                <StatusRow label="Pending" count={result.transactionStatus.pendingCount} total={result.totals.totalRecords} color="yellow" />
              </div>
            </div>

            {/* Reason Codes (if any failures) */}
            {result.reasonCodeBreakdown.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Failure Reasons</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {result.reasonCodeBreakdown.map((rc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-0">
                      <div>
                        <span className="font-mono text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded mr-2">{rc.code}</span>
                        <span className="text-gray-700">{rc.description}</span>
                      </div>
                      <span className="font-semibold text-gray-900">{rc.count} ({rc.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bank-wise Summary */}
          {result.bankWiseSummary.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Bank-wise Summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Bank</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Records</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Success</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Failed</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Rate</th>
                      <th className="text-right py-2 px-3 font-semibold text-gray-600">Amount (Rs)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.bankWiseSummary.slice(0, 20).map((bank, idx) => (
                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-3">
                          <div className="font-medium text-gray-900">{bank.bankName}</div>
                          <div className="text-xs text-gray-500">{bank.bankIIN}</div>
                        </td>
                        <td className="text-right py-2 px-3 font-medium">{bank.count.toLocaleString()}</td>
                        <td className="text-right py-2 px-3 text-green-600 font-medium">{bank.successCount.toLocaleString()}</td>
                        <td className="text-right py-2 px-3 text-red-600 font-medium">{bank.failedCount.toLocaleString()}</td>
                        <td className="text-right py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            bank.successRate >= 90 ? 'bg-green-100 text-green-700' :
                            bank.successRate >= 70 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {bank.successRate}%
                          </span>
                        </td>
                        <td className="text-right py-2 px-3 font-medium">{Number(bank.amountInRupees).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* File Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Request File:</span>
                <span className="ml-2 font-medium text-gray-900">{result.requestFileName}</span>
              </div>
              <div>
                <span className="text-gray-500">Response File:</span>
                <span className="ml-2 font-medium text-gray-900">{result.responseFileName}</span>
              </div>
              <div>
                <span className="text-gray-500">Uploaded by:</span>
                <span className="ml-2 font-medium text-gray-900">{result.uploadedBy}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      {!result && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mt-6">
          <h3 className="text-sm font-semibold text-indigo-800 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            How it works
          </h3>
          <div className="text-xs text-indigo-700 space-y-1">
            <p className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 shrink-0" />
              Upload the APBS REQUEST file (sent to bank) and RESPONSE file (received from bank)
            </p>
            <p className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 shrink-0" />
              The system performs fuzzy name matching using Levenshtein distance algorithm
            </p>
            <p className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 shrink-0" />
              Handles name order variations, spelling differences, and title prefixes (Mrs/Mr/W/O/S/O)
            </p>
            <p className="flex items-center gap-2">
              <ArrowRight className="w-3 h-3 shrink-0" />
              Download a detailed Excel report with 6 analysis sheets
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

const SummaryCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color: string;
}> = ({ label, value, sub, color }) => {
  const colorMap: Record<string, string> = {
    indigo: 'from-indigo-500 to-indigo-600',
    green: 'from-green-500 to-green-600',
    blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
  };
  return (
    <div className={`bg-gradient-to-br ${colorMap[color] || colorMap.indigo} rounded-xl p-4 text-white`}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
      {sub && <p className="text-xs opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
};

const MatchCard: React.FC<{
  label: string;
  count: number;
  total: number;
  color: string;
}> = ({ label, count, total, color }) => {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <div className={`w-3 h-3 ${color} rounded-full mx-auto mb-2`} />
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900">{count.toLocaleString()}</p>
      <p className="text-xs text-gray-500">{pct}%</p>
    </div>
  );
};

const StatusRow: React.FC<{
  label: string;
  count: number;
  total: number;
  color: string;
}> = ({ label, count, total, color }) => {
  const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
  const barColor: Record<string, string> = {
    green: 'bg-green-500',
    red: 'bg-red-500',
    yellow: 'bg-yellow-500',
  };
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-700 font-medium">{label}</span>
        <span className="text-gray-900 font-semibold">{count.toLocaleString()} ({pct}%)</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className={`${barColor[color]} h-2 rounded-full transition-all`}
          style={{ width: `${Math.min(parseFloat(pct), 100)}%` }}
        />
      </div>
    </div>
  );
};

export default ApbsNameMatchingUpload;
